/**
 * 应用路由配置
 * 定义应用的所有路由、权限、菜单等信息
 * 纯配置数据，不包含 React 组件
 */
import React from "react";
import { RouteObject } from "react-router-dom";
import {
  ApartmentOutlined,
  TeamOutlined,
  IdcardOutlined,
  LockOutlined,
  CheckSquareOutlined,
  HomeOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

// 路由配置接口
export interface RouteConfigItem {
  path: string;
  requiredPermissions: string[];
  component: string; // 组件名称
  icon: string; // 图标名称
  label: string; // 菜单标签
  breadcrumb: string; // 面包屑标题
  hideInMenu?: boolean; // 是否在菜单中隐藏
}

// 图标映射
const ICON_MAP = {
  HomeOutlined,
  ApartmentOutlined,
  TeamOutlined,
  IdcardOutlined,
  LockOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
} as const;

/**
 * 路由配置数据
 * 纯配置，不包含 React 元素
 */
export const ROUTE_CONFIG: RouteConfigItem[] = [
  {
    path: "/",
    requiredPermissions: [],
    component: "Navigate", // 特殊标记，表示重定向
    icon: "HomeOutlined",
    label: "首页",
    breadcrumb: "首页",
    hideInMenu: true, // 首页不在菜单中显示
  },
  {
    path: "/services",
    requiredPermissions: ["msadmin:services:view"], // 统一权限检查，超级管理员通过通配符权限自动匹配
    component: "ServicesPage",
    icon: "ApartmentOutlined",
    label: "微服务管理",
    breadcrumb: "微服务管理",
  },
  {
    path: "/admins",
    requiredPermissions: ["msadmin:admins:view"], // 统一权限检查
    component: "AdminsPage",
    icon: "TeamOutlined",
    label: "管理员管理",
    breadcrumb: "管理员管理",
  },
  {
    path: "/roles",
    requiredPermissions: ["msadmin:roles:view"], // 统一权限检查
    component: "RolesPage",
    icon: "IdcardOutlined",
    label: "角色管理",
    breadcrumb: "角色管理",
    hideInMenu: true,
  },
  {
    path: "/operations",
    requiredPermissions: ["msadmin:operations:view"], // 统一权限检查
    component: "OperationsPage",
    icon: "LockOutlined",
    label: "授权操作",
    breadcrumb: "授权操作",
    hideInMenu: true,
  },
  {
    path: "/assignment",
    requiredPermissions: ["msadmin:assignments:view"], // 统一权限检查
    component: "AssignmentPage",
    icon: "CheckSquareOutlined",
    label: "角色管理",
    breadcrumb: "角色管理",
  },
  {
    path: "/usage-docs",
    requiredPermissions: [], // 不需要特定权限，所有登录用户都可以访问
    component: "UsageDocsPage",
    icon: "FileTextOutlined",
    label: "使用说明",
    breadcrumb: "使用说明",
  },
  {
    path: "/api-docs",
    requiredPermissions: [], // 不需要特定权限，所有登录用户都可以访问
    component: "ApiDocsPage",
    icon: "FileTextOutlined",
    label: "接口描述",
    breadcrumb: "接口描述",
  },
];

/**
 * 路由配置类型定义
 */
export interface RouteConfig {
  path: string;
  element: React.ReactElement;
}

/**
 * 页面权限映射
 * 用于快速查找页面所需的权限
 * 直接使用硬编码的三段式权限
 */
export const PAGE_PERMISSIONS = {
  "/services": ["msadmin:services:view"],
  "/admins": ["msadmin:admins:view"],
  "/roles": ["msadmin:roles:view"],
  "/operations": ["msadmin:operations:view"],
  "/assignment": ["msadmin:assignments:view"],
  "/usage-docs": [], // 不需要特定权限
  "/api-docs": [], // 不需要特定权限
} as const;

/**
 * 获取页面所需权限
 * @param path 页面路径
 * @returns 权限数组
 */
export const getPagePermissions = (path: string): string[] => {
  const permissions = PAGE_PERMISSIONS[path as keyof typeof PAGE_PERMISSIONS];
  return permissions ? [...permissions] : [];
};

/**
 * 检查页面是否需要权限验证
 * @param path 页面路径
 * @returns 是否需要权限验证
 */
export const requiresAuth = (path: string): boolean => {
  return path !== "/" && Object.keys(PAGE_PERMISSIONS).includes(path);
};

/**
 * 从路由配置生成菜单项
 * @param navigate 导航函数
 * @returns 菜单项数组
 */
export const getMenuItems = (navigate: (path: string) => void) => {
  return ROUTE_CONFIG.filter((route) => !route.hideInMenu) // 过滤掉隐藏的菜单项
    .map((route) => {
      const IconComponent = ICON_MAP[route.icon as keyof typeof ICON_MAP];
      return {
        key: route.path.replace("/", "") || "home",
        icon: IconComponent ? React.createElement(IconComponent as any) : null,
        label: route.label,
        path: route.path,
        onClick: () => navigate(route.path),
      };
    });
};

/**
 * 根据路径获取路由配置
 * @param path 路径
 * @returns 路由配置项
 */
export const getRouteConfig = (path: string): RouteConfigItem | undefined => {
  return ROUTE_CONFIG.find((route) => route.path === path);
};

/**
 * 根据路径生成面包屑配置
 * @param path 路径
 * @returns 面包屑配置数组
 */
export const getBreadcrumbConfig = (path: string): Array<{ title: string; href?: string }> => {
  const route = getRouteConfig(path);
  if (route) {
    return [{ title: "首页", href: "/" }, { title: route.breadcrumb }];
  }
  return [{ title: "首页", href: "/" }, { title: "微服务管理" }];
};
